import React, { useRef, useEffect, useState } from "react";
import { Box } from "@mui/material";
import VideoCropOverlay, { CropRect } from "./VideoCropOverlay";

interface VideoPlayerProps {
  videoSrc: string;
  currentTime: number;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  trimStart: number;
  trimEnd: number;
  onMetadataLoaded: (duration: number) => void;
  cropMode: boolean;
  cropRect: CropRect;
  setCropRect: (rect: CropRect) => void;
  setVideoSize?: (size: { width: number; height: number }) => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoSrc,
  currentTime,
  isPlaying,
  setIsPlaying,
  setCurrentTime,
  trimStart,
  trimEnd,
  onMetadataLoaded,
  cropMode,
  cropRect,
  setCropRect,
  setVideoSize,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoNaturalSize, setVideoNaturalSize] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!videoRef.current) return;

    if (Math.abs(videoRef.current.currentTime - currentTime) > 0.5) {
      videoRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  useEffect(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      const currentTime = videoElement.currentTime;
      setCurrentTime(currentTime);

      // if (currentTime >= trimEnd) {
      //   videoElement.currentTime = trimStart;
      //   videoElement.pause();
      //   setIsPlaying(false);
      // }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      videoElement.currentTime = trimStart;
    };

    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("ended", handleEnded);

    return () => {
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("ended", handleEnded);
    };
  }, [trimStart, trimEnd, setCurrentTime, setIsPlaying]);

  const handleMetadataLoaded = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    setVideoNaturalSize({
      width: videoWidth,
      height: videoHeight,
    });

    if (setVideoSize) {
      setVideoSize({ width: videoWidth, height: videoHeight });
    }

    if (cropMode && cropRect.width === 0 && cropRect.height === 0) {
      setCropRect({
        x: 0,
        y: 0,
        width: videoWidth,
        height: videoHeight,
      });
    }

    onMetadataLoaded(video.duration);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        bgcolor: "#000",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <video
        ref={videoRef}
        src={videoSrc}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: "400px",
          objectFit: "contain",
          display: "block",
        }}
        onLoadedMetadata={handleMetadataLoaded}
        onClick={() => setIsPlaying(!isPlaying)}
      />

      {cropMode && videoNaturalSize.width > 0 && (
        <VideoCropOverlay
          videoWidth={videoNaturalSize.width}
          videoHeight={videoNaturalSize.height}
          cropRect={cropRect}
          setCropRect={setCropRect}
        />
      )}
    </Box>
  );
};

export default VideoPlayer;
